import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { getIntl } from '../../utils/intl';
import useNotify from '../useNotify';
import urls from '../../utils/urls';
import { appAPI } from '../../utils/api';
import { getProfileTransferRequestsCacheKey, onTransferRequestResponse, onTransferRequestUpdated } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';

const intl = getIntl('useProfileTransferRequest', {
    errorLoadingProfileTransferRequest: 'Error occurred loading profile transfer requests',
    successfullyCreatedTransferRequest: 'Successfully created ownership transfer request',
    errorCreatingTransferRequest: 'Error occurred creating ownership transfer request',
    successfullyRevokedTransferRequest: 'Successfully revoked ownership transfer request',
    errorRevokingTransferRequest: 'Error occurred revoking ownership transfer request',
    successfullyAcceptedTransferRequest: 'Successfully accepted ownership transfer request',
    successfullyRejectedTransferRequest: 'Successfully rejected ownership transfer request',
    errorRespondingToTransferRequest: 'Error occurred responding to ownership transfer request',
});

export type UseProfileTransferRequestArgs = {
    profileId?: string;
    onSendTransferRequest?: () => void;
    onRevokeTransferRequest?: () => void;
    onRespondToTransferRequest?: () => void;
};

export type UseProfileTransferRequestReturnValue = {
    profileTransferRequest: APITypes.medicalProfileService.ProfileOutboundTransferResponse;
    isLoadingProfileTransferRequest: boolean;
    hasPendingRequest: boolean;
    sendTransferRequest: (
        request: APITypes.medicalProfileService.CreateTransferRequestRequest & { profileId: string },
    ) => void;
    isSendingTransferRequest: boolean;
    revokeTransferRequest: (profileId: string) => void;
    isRevokingTransferRequest: boolean;
    respondToTransferRequest: (
        request: APITypes.medicalProfileService.TransferRequestDecisionRequest & { profileId: string },
    ) => void;
    isRespondingToTransferRequest: boolean;
};

export default function useProfileTransferRequest({
    profileId,
    onSendTransferRequest,
    onRespondToTransferRequest,
    onRevokeTransferRequest,
}: UseProfileTransferRequestArgs): UseProfileTransferRequestReturnValue {
    const notify = useNotify();

    const profileTransferRequestQuery = useQuery({
        queryKey: getProfileTransferRequestsCacheKey(profileId),
        queryFn: async () => {
            return (await appAPI.get(
                urls.api.ownershipTransfer.profileTransferRequest(profileId!),
            )) as APITypes.medicalProfileService.ProfileOutboundTransferResponse;
        },
        initialData: {
            status: 'no-pending-transfer-request',
        } as APITypes.medicalProfileService.ProfileOutboundTransferResponse,
        enabled: !!profileId,
    });
    useQueryErrorNotify(profileTransferRequestQuery, intl('errorLoadingProfileTransferRequest'));

    const createTransferRequestMutation = useMutation({
        mutationFn: async ({
            profileId,
            ...body
        }: APITypes.medicalProfileService.CreateTransferRequestRequest & { profileId: string }) => {
            return await appAPI.post(urls.api.ownershipTransfer.createTransferRequest(profileId), {
                body,
            });
        },
        onError: () => {
            notify('error', intl('errorCreatingTransferRequest'));
        },
        onSuccess: () => {
            notify('success', intl('successfullyCreatedTransferRequest'));
            onSendTransferRequest?.();
            onTransferRequestUpdated.resetCache({ medicalProfileId: profileId })();
        },
    });

    const revokeTransferRequest = useMutation({
        mutationFn: async (profileId: string) => {
            return await appAPI.put(urls.api.ownershipTransfer.revokeTransferRequest(profileId), {});
        },
        onError: () => {
            notify('error', intl('errorRevokingTransferRequest'));
        },
        onSuccess: (_data) => {
            notify('success', intl('successfullyRevokedTransferRequest'));
            onRevokeTransferRequest?.();
            onTransferRequestUpdated.resetCache({ medicalProfileId: profileId })();
        },
    });

    const respondToTransferRequest = useMutation({
        mutationFn: async ({
            profileId,
            ...request
        }: APITypes.medicalProfileService.TransferRequestDecisionRequest & { profileId: string }) => {
            return await appAPI.put(urls.api.ownershipTransfer.respondToTransferRequest(profileId), {
                body: request,
            });
        },
        onError: () => {
            notify('error', intl('errorRespondingToTransferRequest'));
        },
        onSuccess: (_data, input) => {
            notify(
                'success',
                intl(
                    input.decision === 'accept'
                        ? 'successfullyAcceptedTransferRequest'
                        : 'successfullyRejectedTransferRequest',
                ),
            );
            onTransferRequestResponse.resetCache({ medicalProfileId: profileId })();
            onRespondToTransferRequest?.();
        },
    });

    return {
        hasPendingRequest:
            profileTransferRequestQuery.data.status === 'pending-collaborator-transfer-request' ||
            profileTransferRequestQuery.data.status === 'pending-non-collaborator-transfer-request',
        profileTransferRequest: profileTransferRequestQuery.data,
        isLoadingProfileTransferRequest: profileTransferRequestQuery.isLoading,
        sendTransferRequest: createTransferRequestMutation.mutateAsync,
        isSendingTransferRequest: createTransferRequestMutation.isPending,
        revokeTransferRequest: revokeTransferRequest.mutateAsync,
        isRevokingTransferRequest: respondToTransferRequest.isPending,
        respondToTransferRequest: respondToTransferRequest.mutateAsync,
        isRespondingToTransferRequest: respondToTransferRequest.isPending,
    };
}
